import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import type { Command, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { NodeType, MarkType } from '@atlaskit/editor-prosemirror/model';
import { TextSelection, type Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { SmartLinkSummarizeInvokedFrom } from '@atlassian/editor-plugin-ai-definitions/types';

import type { AIPlugin } from '../../aiPluginType';
import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../types/types';
import type {
	EditorPluginAIConfigItem,
	EditorPluginAIConfigItemFactoryOptions,
} from '../prebuilt/config-items/config-items';
import { summarizeSmartlink } from '../prebuilt/config-items/prompts/summarize-smartlink/summarize-smartlink';
import type { InvokedFrom, InvokedFromTriggerMethod } from '../utils/analytics/analyticsFlowTypes';

type FireAnalyticsEvent = NonNullable<
	NonNullable<ExtractInjectionAPI<AIPlugin>['analytics']>['actions']
>['fireAnalyticsEvent'];

const SMART_LINK_SUMMARIZE_BUTTON_INVOKED_FROM: SmartLinkSummarizeInvokedFrom =
	ACTION_SUBJECT_ID.SMART_LINK_SUMMARIZE_BUTTON;

export type SmartLinkSummarizePopupArgs = {
	from: number;
	has3pSources?: boolean;
	invokedFrom?: SmartLinkSummarizeInvokedFrom;
	smartlinkUrl: string;
	sourceProduct?: string | null;
	to: number;
};

export type CreateSmartLinkSummarizeConfigItemOptions = EditorPluginAIConfigItemFactoryOptions & {
	smartlinkUrl: string;
};

export type CreateSmartLinkSummarizeCommandOptions = {
	aiGlobalOptIn: AIGlobalOptIn;
	decorateDeselectTransaction?: (tr: Transaction) => Transaction;
	fireAnalyticsEvent?: FireAnalyticsEvent;
	has3pSources?: boolean;
	lastTriggeredFrom?: InvokedFrom;
	locale?: string;
	nodeRange?: { from: number; to: number };
	nodeType?: NodeType;
	openSmartLinkSummarizePopup?: (args: SmartLinkSummarizePopupArgs) => void;
	smartlinkUrl: string;
	sourceProduct?: string | null;
	triggeredForMark?: MarkType;
	triggerMethod?: InvokedFromTriggerMethod;
};

export type OpenSmartLinkSummarizeOptions = CreateSmartLinkSummarizeCommandOptions & {
	editorView: EditorView;
};

/**
 * Creates the existing Smart Link summarize config item.
 */
export const createSmartLinkSummarizeConfigItem = (
	options: CreateSmartLinkSummarizeConfigItemOptions,
): EditorPluginAIConfigItem => summarizeSmartlink(options);

/**
 * Creates a command that invokes the existing Smart Link summarize flow.
 */
export function createSmartLinkSummarizeCommand({
	aiGlobalOptIn,
	decorateDeselectTransaction,
	fireAnalyticsEvent,
	has3pSources,
	lastTriggeredFrom = 'floatingToolbar',
	locale,
	nodeRange,
	nodeType,
	openSmartLinkSummarizePopup,
	smartlinkUrl,
	sourceProduct,
	triggeredForMark,
	triggerMethod = 'click',
}: CreateSmartLinkSummarizeCommandOptions): Command {
	return (state, dispatch, view) => {
		fireAnalyticsEvent?.({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			eventType: EVENT_TYPE.UI,
			actionSubjectId: ACTION_SUBJECT_ID.SMART_LINK_SUMMARIZE_BUTTON,
			attributes: {
				sourceProduct,
				has3pSources,
			},
		});

		if (openSmartLinkSummarizePopup) {
			const { from, to } = nodeRange ?? state.selection;

			if (dispatch) {
				const deselectAt = Math.min(to + 1, state.doc.content.size);
				let tr = state.tr.setSelection(TextSelection.near(state.doc.resolve(deselectAt), 1));
				if (decorateDeselectTransaction) {
					tr = decorateDeselectTransaction(tr);
				}
				dispatch(tr);
			}

			openSmartLinkSummarizePopup({
				from,
				to,
				smartlinkUrl,
				sourceProduct,
				has3pSources,
				...(lastTriggeredFrom === SMART_LINK_SUMMARIZE_BUTTON_INVOKED_FROM
					? { invokedFrom: lastTriggeredFrom }
					: {}),
			});
			return false;
		}

		const configItem = summarizeSmartlink({ smartlinkUrl });
		const openAIModalCommand = createOpenAIModalCommand({
			state,
			configItem,
			lastTriggeredFrom,
			triggerMethod,
			aiGlobalOptIn,
			locale,
			triggeredFor: nodeType,
			sourceProduct,
			has3pSources,
			triggeredForMark,
		});
		openAIModalCommand(state, dispatch, view);
		return false;
	};
}

/**
 * Opens the existing Smart Link summarize flow from any caller-owned UI.
 */
export function openSmartLinkSummarize({
	editorView,
	...commandOptions
}: OpenSmartLinkSummarizeOptions): void {
	const command = createSmartLinkSummarizeCommand(commandOptions);
	command(editorView.state, editorView.dispatch, editorView);
}
